// KOA-5857 — Binary cell editor for opaque-byte columns.
//
// `FileContent`, `ByteArray`, `VarBinary`, `Binary`, `Bytea` — these
// hold bytes we can't reason about, so the cell renders a typed chip
// + Download + Replace + Clear rather than trying to preview content.
// Image / Signature have their own editor that actually decodes pixels.
//
// Layout in the 32 px cell:
//
//   PDF · 1.2 MB  [↓] [Replace ×]
//
// Improvements over the previous wrapper:
//   - Label uses magic-byte detection (`detectBinaryKind`) so the chip
//     reads `PDF · 1.2 MB`, `ZIP · 4.5 MB`, `MP3 · 320 KB` instead of
//     `bytea · 1.2 MB`. Falls back to "Binary" when bytes are opaque.
//   - Download saves the bytes as a real file, named from the column,
//     with an extension matching the detected MIME.
//   - Pre-load size guard rejects files above MAX_BYTES before the
//     base64 read kicks in — a 200 MB file would otherwise lock the
//     grid for seconds while it decoded. Bound surfaces as an inline
//     error chip; the previous value (if any) is kept.

import React, { useEffect, useRef, useState } from "react";
import KuikaFileInput from "../ui-kit/kuika-file-input/kuika-file-input";
import {
  approxBase64Bytes,
  detectBinaryKind,
  detectBinaryKindAsync,
  downloadBase64,
  formatByteSize,
  IDetectedBinary,
  readFileAsBase64,
  sanitiseFileNamePart,
  shortRowKeyForFilename
} from "./binary-cell-helpers";
import DownloadFilenamePopover from "./download-filename-popover";

// 50 MB on disk → ~67 MB base64. Beyond this the grid stalls on every
// re-render while it ferries the string around state. Anyone moving
// truly large blobs should be doing it through a proper upload flow,
// not a database cell.
const MAX_BYTES: number = 50 * 1024 * 1024;

export interface IBinaryCellEditorProps {
  value: unknown;
  // Column name — used for the suggested download filename.
  columnName?: string | null;
  // Row's primary-key value — appended to the download filename so
  // multiple rows of the same table don't all download as
  // `<column>.<ext>`. See `shortRowKeyForFilename` for the
  // GUID-head / numeric / truncated-string rules.
  rowKey?: string | null;
  // Per-column byte cap derived from the SQL `varbinary(N)` declaration.
  // `null` or `undefined` means "unknown" (treat as unlimited); `-1`
  // means `varbinary(max)` (also unlimited). Any other positive value
  // is the hard byte ceiling — the editor refuses a pick that would
  // exceed it instead of letting SQL Server fail mid-batch with
  // "String or binary data would be truncated."
  columnMaxBytes?: number | null;
  disabled?: boolean;
  onChange: (next: unknown) => void;
  onBlur?: () => void;
}

const BinaryCellEditor: React.FC<IBinaryCellEditorProps> = (props: IBinaryCellEditorProps): JSX.Element => {
  const { value, columnName, rowKey, columnMaxBytes, disabled, onChange, onBlur } = props;
  const raw: string = typeof value === "string" ? value : "";
  const hasValue: boolean = raw.length > 0;

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [downloadPopoverOpen, setDownloadPopoverOpen] = useState<boolean>(false);
  const downloadButtonRef = useRef<HTMLButtonElement | null>(null);

  // Start with the synchronous detector (covers common formats with
  // no decode cost) and upgrade asynchronously via the file-type
  // library once it loads. The async detector recognises ~500 formats
  // and avoids the base64-alignment traps the hand-rolled prefixes
  // are prone to.
  const [detected, setDetected] = useState<IDetectedBinary>((): IDetectedBinary =>
    hasValue ? detectBinaryKind(raw) : { mime: "application/octet-stream", label: "Binary", ext: "bin" }
  );
  useEffect((): (() => void) => {
    if (!hasValue) {
      setDetected({ mime: "application/octet-stream", label: "Binary", ext: "bin" });
      return (): void => {};
    }
    setDetected(detectBinaryKind(raw));
    let cancelled: boolean = false;
    void detectBinaryKindAsync(raw).then((next: IDetectedBinary): void => {
      if (cancelled) {
        return;
      }
      setDetected(next);
    });
    return (): void => {
      cancelled = true;
    };
  }, [hasValue, raw]);

  const sizeText: string = hasValue ? formatByteSize(approxBase64Bytes(raw)) : "";
  const summaryText: string = hasValue ? `${detected.label} · ${sizeText}` : "";

  const baseName: string =
    typeof columnName === "string" && columnName.length > 0 ? sanitiseFileNamePart(columnName) : "value";
  // Suffix with a short row-key fragment when the row has a PK so
  // downloading the same column from different rows produces
  // distinct filenames instead of stacking as `(1)`, `(2)` in the
  // browser's Downloads folder.
  const rowKeyShort: string = shortRowKeyForFilename(rowKey);
  const downloadFilename: string =
    rowKeyShort.length > 0 ? `${baseName}-${rowKeyShort}.${detected.ext}` : `${baseName}.${detected.ext}`;

  const handleDownloadConfirm = (filename: string): void => {
    if (raw.length === 0) {
      return;
    }
    const outcome = downloadBase64(raw, detected.mime, filename);
    if (!outcome.ok) {
      setError(outcome.errorMessage ?? "Couldn't download this file.");
      // Keep the popover open so the user sees the error in context
      // rather than wondering why nothing happened.
      return;
    }
    setError(null);
    setDownloadPopoverOpen(false);
  };

  const handlePick = async (file: File | null): Promise<void> => {
    setError(null);
    if (file === null) {
      onChange(null);
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`${formatByteSize(file.size)} exceeds the ${formatByteSize(MAX_BYTES)} inline limit.`);
      return;
    }
    // Per-column ceiling — defined by the SQL `varbinary(N)` width.
    // A `varbinary` declared without a length defaults to `varbinary(1)`
    // on SQL Server, which would fail at save time with "String or
    // binary data would be truncated." Fail here with a column-aware
    // message instead.
    if (typeof columnMaxBytes === "number" && columnMaxBytes > 0 && file.size > columnMaxBytes) {
      setError(
        `${formatByteSize(file.size)} exceeds the column's ${formatByteSize(columnMaxBytes)} limit. Resize the column in the schema editor or pick a smaller file.`
      );
      return;
    }
    setBusy(true);
    try {
      const b64: string = await readFileAsBase64(file);
      onChange(b64);
    } catch {
      setError("Couldn't read this file.");
    } finally {
      setBusy(false);
    }
  };

  // Empty state — single picker.
  if (!hasValue) {
    return (
      <span className="kuika-binary-cell kuika-binary-cell--empty">
        <KuikaFileInput
          variant="cell"
          size="sm"
          busy={busy}
          disabled={disabled}
          emptyLabel="Choose file…"
          onChange={(file: File | null): void => {
            void handlePick(file);
          }}
          onBlur={onBlur}
        />
        {error !== null && (
          <span className="kuika-binary-cell__error" role="alert">
            {error}
          </span>
        )}
      </span>
    );
  }

  return (
    <span className="kuika-binary-cell">
      <span className="kuika-binary-cell__meta" title={summaryText}>
        {summaryText}
      </span>
      <button
        ref={downloadButtonRef}
        type="button"
        className="kuika-binary-cell__icon-btn"
        onClick={(): void => setDownloadPopoverOpen((open: boolean): boolean => !open)}
        aria-label={`Download ${detected.label}`}
        aria-haspopup="dialog"
        aria-expanded={downloadPopoverOpen}
        title="Download"
        disabled={disabled || busy}
      >
        ↓
      </button>
      {downloadPopoverOpen && downloadButtonRef.current !== null && (
        <DownloadFilenamePopover
          anchor={downloadButtonRef.current}
          defaultFilename={downloadFilename}
          onConfirm={handleDownloadConfirm}
          onClose={(): void => setDownloadPopoverOpen(false)}
        />
      )}
      <KuikaFileInput
        variant="cell"
        size="sm"
        busy={busy}
        valueLabel="Replace"
        showClear={true}
        disabled={disabled}
        className="kuika-binary-cell__file"
        onChange={(file: File | null): void => {
          void handlePick(file);
        }}
        onBlur={onBlur}
      />
      {error !== null && (
        <span className="kuika-binary-cell__error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
};

export default BinaryCellEditor;
